/**
 * Sessions Contract — The typed API contract for the Sessions domain.
 *
 * Sessions are the core unit of focused research in Virza:
 * a structured, goal-oriented environment for inquiry.
 *
 * BACKEND ALIGNMENT:
 * - Follows RFC 9457 ProblemDetail for errors
 * - Supports cursor + offset pagination with HATEOAS links
 * - All timestamps are ISO 8601 strings
 *
 * MIGRATION NOTE:
 * Zod schemas are re-exported from auto-generated code (@hey-api/openapi-ts).
 * Types inferred from generated schemas. UI-only constructs remain hand-written.
 */

import { z } from 'zod';
import type { PaginatedResponse, PaginationParams, SortParams, ApiResponse } from './shared';

// Re-export generated schemas
import {
  zSessionStatus as GeneratedSessionStatus,
  zSessionPhase as GeneratedSessionPhase,
  zSessionPriority as GeneratedSessionPriority,
  zSessionCategory as GeneratedSessionCategory,
  zSessionGoal as GeneratedSessionGoal,
  zSessionInsight as GeneratedSessionInsight,
  zSessionCollaborator as GeneratedSessionCollaborator,
  zSessionSummaryRead as GeneratedSessionSummaryRead,
  zSessionRead as GeneratedSessionRead,
  zInsightType as GeneratedInsightType,
  zConfidenceLevel as GeneratedConfidenceLevel,
} from '@/api/generated/hey-api/zod.gen';

// ─── Enums ─────────────────────────────────────────────────────────────

export const SessionStatusEnum = GeneratedSessionStatus;
export type SessionStatus = z.infer<typeof SessionStatusEnum>;

export const SessionPhaseEnum = GeneratedSessionPhase;
export type SessionPhase = z.infer<typeof SessionPhaseEnum>;

export const SessionPriorityEnum = GeneratedSessionPriority;
export type SessionPriority = z.infer<typeof SessionPriorityEnum>;

export const SessionCategoryEnum = GeneratedSessionCategory;
export type SessionCategory = z.infer<typeof SessionCategoryEnum>;

// ─── Nested DTOs ───────────────────────────────────────────────────────

export const SessionGoalSchema = GeneratedSessionGoal;
export type SessionGoal = z.infer<typeof SessionGoalSchema>;

export const SessionInsightSchema = GeneratedSessionInsight;
export type SessionInsight = z.infer<typeof SessionInsightSchema>;

export const InsightTypeEnum = GeneratedInsightType;
export type InsightType = z.infer<typeof InsightTypeEnum>;

export const ConfidenceLevelEnum = GeneratedConfidenceLevel;
export type ConfidenceLevel = z.infer<typeof ConfidenceLevelEnum>;

export const SessionCollaboratorSchema = GeneratedSessionCollaborator;
export type SessionCollaborator = z.infer<typeof SessionCollaboratorSchema>;

// ─── Session Summary DTO (list views) ──────────────────────────────────

export const SessionSummaryDTOSchema = GeneratedSessionSummaryRead;
export type SessionSummaryDTO = z.infer<typeof SessionSummaryDTOSchema>;

// ─── Full Session DTO (detail view) ────────────────────────────────────

export const SessionDTOSchema = GeneratedSessionRead;
export type SessionDTO = z.infer<typeof SessionDTOSchema>;

// ─── Request Types ─────────────────────────────────────────────────────
// UI-only: Request interfaces for frontend query/mutation composition.

export interface SessionListFilters {
  status?: SessionStatus;
  phase?: SessionPhase;
  priority?: SessionPriority;
  category?: SessionCategory;
  tags?: string[];
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export type SessionSortField =
  | 'lastActive'
  | 'created'
  | 'title'
  | 'priority'
  | 'deadline'
  | 'progress';

export interface SessionListParams
  extends PaginationParams, SortParams<SessionSortField>, SessionListFilters {}

export interface CreateSessionRequest {
  title: string;
  description: string;
  objective: string;
  category: SessionCategory;
  priority?: SessionPriority;
  tags?: string[];
  color?: string;
  templateId?: string;
  workspaceId: string;
}

export interface UpdateSessionRequest {
  title?: string;
  description?: string;
  objective?: string;
  status?: SessionStatus;
  phase?: SessionPhase;
  priority?: SessionPriority;
  category?: SessionCategory;
  tags?: string[];
  color?: string;
}

// ─── Goal mutations ────────────────────────────────────────────────────

export interface AddGoalRequest {
  text: string;
  order?: number;
}

export interface UpdateGoalRequest {
  text?: string;
  isCompleted?: boolean;
  order?: number;
}

// ─── Insight mutations ─────────────────────────────────────────────────

export interface AddInsightRequest {
  content: string;
  type?: InsightType;
  confidence?: ConfidenceLevel;
  sourceIds?: string[];
  conversationId?: string;
}

// ─── Response Types ────────────────────────────────────────────────────
// UI-only: Response type aliases for frontend convenience.

export type SessionListResponse = PaginatedResponse<SessionSummaryDTO>;
export type SessionDetailResponse = ApiResponse<SessionDTO>;
export type CreateSessionResponse = ApiResponse<SessionDTO>;
export type UpdateSessionResponse = ApiResponse<SessionDTO>;
export type DeleteSessionResponse = ApiResponse<{ id: string; deleted: boolean }>;

// ─── Adapter Interface ─────────────────────────────────────────────────
// UI-only: Adapter interface for dependency inversion (mock/HTTP).

export interface ISessionsAdapter {
  list(params: SessionListParams): Promise<SessionListResponse>;
  getById(id: string): Promise<SessionDetailResponse>;
  create(data: CreateSessionRequest): Promise<CreateSessionResponse>;
  update(id: string, data: UpdateSessionRequest): Promise<UpdateSessionResponse>;
  delete(id: string): Promise<DeleteSessionResponse>;
  // Goal mutations — return updated full session
  addGoal(sessionId: string, data: AddGoalRequest): Promise<SessionDetailResponse>;
  updateGoal(
    sessionId: string,
    goalId: string,
    data: UpdateGoalRequest,
  ): Promise<SessionDetailResponse>;
  // Insight mutations — return updated full session
  addInsight(sessionId: string, data: AddInsightRequest): Promise<SessionDetailResponse>;
}
